import './LoiComparisonLegalIssues.scss';
import React, { useState } from 'react';
import { LoiComparisonFormDto } from '../../../../slices/marketplace/loi-comparison/models/loi-comparison-form.model';
import { LoiFormSection } from '../../../../slices/marketplace/loi-form/loi-form-section';
import { LegalIssueItem } from '../../../../slices/marketplace/loi-form/legal-issue-item';
import { isLegalIssueVisibleByLogic } from '../../../../slices/marketplace/loi-form/legal-issue-visibility';
import { getOrderedItemsForSection } from '../../../../slices/marketplace/loi-form/get-ordered-items-for-section';
import { hasValueForLinkedField } from '../../../../slices/marketplace/loi-form/has-value-for-linked-field';
import {
    LoiFormAnswerType,
    getOptionsForAnswerType,
} from '../../../../slices/marketplace/loi-form/loi-form-answer-type.enum';
import { TermsFormLinkedField } from '../../../../slices/marketplace/loi-form/terms-form-linked-field';
import CollapsibleComponent from '../../../../components/Collapsible/CollapsibleComponent';
import InfoTooltip from '../../../../components/info-tooltip/info-tooltip';
import BuyerProfilePreviewPopup from '../../../../components/BuyerProfilePreviewPopup/BuyerProfilePreviewPopup';

const EMPTY = '—';
const LABEL_COLUMN_FR = 1.5;
const MAX_HEADER_LEVEL = 5;

interface SectionConfig {
    section: LoiFormSection;
    templateItems: LegalIssueItem[];
    getItems: (dto: LoiComparisonFormDto) => LegalIssueItem[];
    applyLinkedField: boolean;
}

const resolveValue = (item: LegalIssueItem): string => {
    if (!item.value) return EMPTY;
    if (
        item.answerType === LoiFormAnswerType.PurchaseAgreementType ||
        item.answerType === LoiFormAnswerType.LeaseRateUponExtension
    ) {
        const options = item.options?.length
            ? item.options
            : getOptionsForAnswerType(item.answerType);
        return options.find((o) => o.value === item.value)?.label ?? item.value;
    }
    return item.value;
};

const resolveBuyerCellValue = (
    dto: LoiComparisonFormDto,
    item: LegalIssueItem,
    getItems: (dto: LoiComparisonFormDto) => LegalIssueItem[],
    linkedField: TermsFormLinkedField | null
): string => {
    if (!dto.form) return EMPTY;
    if (
        linkedField != null &&
        !hasValueForLinkedField(dto.form.termForm, linkedField)
    )
        return EMPTY;
    const buyerItems = getItems(dto);
    const buyerItem = buyerItems.find((i) => i.customId === item.customId);
    if (!buyerItem || !isLegalIssueVisibleByLogic(buyerItems, buyerItem))
        return EMPTY;
    return resolveValue(buyerItem);
};

export interface LoiComparisonLegalIssuesProps {
    items: LoiComparisonFormDto[];
    mode: 'legalIssues' | 'covenants';
}

export const LoiComparisonLegalIssues = ({
    items,
    mode,
}: LoiComparisonLegalIssuesProps) => {
    const templateForm = items.find((dto) => dto.form !== null)?.form;
    const [openSectionId, setOpenSectionId] = useState<string | null>(null);
    const [selectedBuyer, setSelectedBuyer] = useState<LoiComparisonFormDto | null>(null);

    if (!templateForm) return null;

    const sortedSections = [...templateForm.legalIssueSections].sort(
        (a, b) => a.order - b.order
    );

    const covenantSections = sortedSections.filter((section) =>
        templateForm.additionalCovenants?.some(
            (c) => c.sectionId === section.id
        )
    );

    const sectionConfigs: SectionConfig[] =
        mode === 'covenants'
            ? covenantSections.map((section) => ({
                  section,
                  templateItems: templateForm.additionalCovenants ?? [],
                  getItems: (dto: LoiComparisonFormDto) =>
                      dto.form?.additionalCovenants ?? [],
                  applyLinkedField: false,
              }))
            : sortedSections.map((section) => ({
                  section,
                  templateItems: templateForm.legalIssues,
                  getItems: (dto: LoiComparisonFormDto) =>
                      dto.form?.legalIssues ?? [],
                  applyLinkedField: true,
              }));

    const gridColumns = `${LABEL_COLUMN_FR}fr repeat(${items.length}, 1fr)`;

    const renderSection = ({
        section,
        templateItems,
        getItems,
        applyLinkedField,
    }: SectionConfig) => {
        const linkedField = applyLinkedField
            ? (section.termsFormLinkedField ?? null)
            : null;

        if (
            linkedField != null &&
            !items.some((dto) =>
                hasValueForLinkedField(dto.form?.termForm, linkedField)
            )
        ) {
            return null;
        }

        const sectionItems = getOrderedItemsForSection(
            section.id,
            templateItems
        );
        if (sectionItems.length === 0) return null;

        const annotated = sectionItems.reduce<
            { item: LegalIssueItem; activeLevel: number }[]
        >((acc, item) => {
            const prev = acc.length > 0 ? acc[acc.length - 1].activeLevel : 1;
            const activeLevel =
                item.headerLevel != null && item.headerLevel >= 1
                    ? item.headerLevel
                    : prev;
            acc.push({ item, activeLevel });
            return acc;
        }, []);

        const rows = annotated.map(({ item, activeLevel }) => {
            const level = item.headerLevel;
            const rowClass =
                level === 1
                    ? 'loi-comparison-row-header level-1'
                    : level != null && level >= 2
                      ? `loi-comparison-row-subheader level-${Math.min(level, MAX_HEADER_LEVEL)}`
                      : 'loi-comparison-row-item';
            const itemUnderClass =
                rowClass === 'loi-comparison-row-item'
                    ? ` item-under-${Math.min(activeLevel, MAX_HEADER_LEVEL)}`
                    : '';

            return (
                <div
                    key={item.id}
                    className={`loi-comparison-row ${rowClass}${itemUnderClass}`}
                    style={{ gridTemplateColumns: gridColumns }}>
                    <div className="loi-comparison-cell loi-comparison-cell-name">
                        <div className="loi-comparison-cell-name-with-tooltip">
                            <span>{item.name}</span>
                            {item.tooltip && (
                                <InfoTooltip
                                    text={item.tooltip}
                                    idText={`${section.id}-${item.id}`}
                                    className="loi-comparison-name-tooltip"
                                />
                            )}
                        </div>
                    </div>
                    {items.map((dto) => {
                        const value =
                            !item.showValueInput
                                ? ''
                                : resolveBuyerCellValue(
                                      dto,
                                      item,
                                      getItems,
                                      linkedField
                                  );
                        return (
                            <div
                                key={dto.buyerId}
                                className="loi-comparison-cell loi-comparison-cell-value">
                                <span className="loi-comparison-value-readonly">
                                    {value}
                                </span>
                            </div>
                        );
                    })}
                </div>
            );
        });

        return (
            <CollapsibleComponent
                key={section.id}
                isExpanded={openSectionId === section.id}
                onToggle={(expanded) =>
                    setOpenSectionId(expanded ? section.id : null)
                }
                header={section.name}>
                <div className="loi-comparison-section-body">
                    <div className="loi-comparison-grid">{rows}</div>
                </div>
            </CollapsibleComponent>
        );
    };

    return (
        <div style={{ overflow: 'auto' }}>
            <div className="loi-comparison-legal-issues">
                <div className="loi-comparison-grid loi-comparison-grid-header">
                    <div
                        className="loi-comparison-row loi-comparison-row-head"
                        style={{ gridTemplateColumns: gridColumns }}>
                        <div className="loi-comparison-cell loi-comparison-cell-name" />
                        {items.map((dto) => (
                            <div
                                key={dto.buyerId}
                                className="loi-comparison-cell loi-comparison-cell-value">
                                <span
                                    className="clickable-text"
                                    onClick={() =>
                                        setSelectedBuyer(dto)
                                    }>
                                    {dto.buyerName}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
                {sectionConfigs.map(renderSection)}
                {selectedBuyer != null && (
                    <BuyerProfilePreviewPopup
                        buyerId={selectedBuyer.buyerId}
                        close={() => setSelectedBuyer(null)}
                        showLoiTabs={true}
                        loiFormOverride={selectedBuyer.form}
                    />
                )}
            </div>
        </div>
    );
};
